/**
 * recist.js — динамика опухолевого ответа по RECIST 1.1. Чистая математика
 * без импортов; проверяется в node на случаях, посчитанных на бумаге
 * (tests/recist_test.mjs).
 *
 * ЗАЧЕМ. Слова владельца: «онкологический приём: … те же очаги, динамика по
 * RECIST». Врач меряет очаги в разных исследованиях; модуль складывает
 * диаметры целевых очагов и говорит стандартный ответ: CR / PR / SD / PD.
 *
 * ПРАВИЛА (RECIST 1.1, Eisenhauer 2009) — как они здесь закодированы:
 *   · целевых очагов ≤ 5, не больше 2 на орган; орган берётся из имени очага
 *     до дефиса («печень-1» → печень), безымянный орган правило на пары
 *     не проверяет — модуль честно говорит об этом предупреждением;
 *   · измеримость в базе: НЕ-узел — длинник ≥ 10 мм; лимфоузел — КОРОТКАЯ
 *     ось ≥ 15 мм (и в сумму узлы всегда входят короткой осью);
 *   · сумма = Σ длинников не-узлов + Σ коротких осей узлов;
 *   · CR — все не-узлы исчезли (0 мм) И у всех целевых узлов короткая ось
 *     < 10 мм (узлы «до нуля» не худеют — это норма стандарта, не ошибка);
 *   · PD — рост суммы ОТ НАДИРА (наименьшей из ПРЕЖНИХ сумм, включая базу)
 *     на ≥ 20% И ≥ 5 мм абсолютно, ЛИБО появление новых очагов;
 *   · PR — падение суммы ОТ БАЗЫ на ≥ 30%;
 *   · SD — всё остальное. Порядок проверки: новые → CR → PD → PR → SD
 *     (так PD по новым очагам не маскируется красивым уменьшением целевых).
 *
 * ЧЕГО МОДУЛЬ НЕ ДЕЛАЕТ: не оценивает нецелевые очаги (кроме флага «новые»),
 * не подтверждает CR/PR повторным исследованием (это правило времени, а не
 * арифметики) — об обоих ограничениях сказано в окне.
 */

const ПД_ДОЛЯ = 0.20, ПД_АБС_ММ = 5, ПР_ДОЛЯ = 0.30;
const ИЗМЕРИМ_НЕУЗЕЛ_ММ = 10, ИЗМЕРИМ_УЗЕЛ_ММ = 15, УЗЕЛ_НОРМА_ММ = 10;
const ЦЕЛЕЙ_МАКС = 5, НА_ОРГАН_МАКС = 2;

const _орган = (имя) => {
  const м = String(имя || '').match(/^([^\-–—]+)[\-–—]/);
  return м ? м[1].trim().toLowerCase() : null;
};

/**
 * Отобрать целевые очаги из базового исследования.
 * @param {Array<{имя:string, мм:number, узел?:boolean}>} базовыеОчаги
 * @param {string[]} [именаЦелей] — выбор врача; без него берутся все годные
 *        по убыванию размера до потолка.
 * @returns {{цели:string[], отвергнутые:Array<{имя,почему}>, предупреждения:string[]}}
 */
export function проверитьЦели(базовыеОчаги, именаЦелей = null) {
  const отвергнутые = [], предупреждения = [];
  const кандидаты = (базовыеОчаги || []).filter((о) => {
    if (!о || !о.имя || !Number.isFinite(о.мм)) { отвергнутые.push({ имя: о?.имя || '(без имени)', почему: 'нет имени или размера' }); return false; }
    if (о.узел && о.мм < ИЗМЕРИМ_УЗЕЛ_ММ) { отвергнутые.push({ имя: о.имя, почему: `узел с короткой осью ${о.мм} мм < ${ИЗМЕРИМ_УЗЕЛ_ММ} — по RECIST не целевой` }); return false; }
    if (!о.узел && о.мм < ИЗМЕРИМ_НЕУЗЕЛ_ММ) { отвергнутые.push({ имя: о.имя, почему: `длинник ${о.мм} мм < ${ИЗМЕРИМ_НЕУЗЕЛ_ММ} — по RECIST не измерим` }); return false; }
    return true;
  });
  let выбранные = именаЦелей
    ? кандидаты.filter((о) => именаЦелей.includes(о.имя))
    : [...кандидаты].sort((а, б) => б.мм - а.мм);
  if (именаЦелей) {
    for (const имя of именаЦелей) {
      if (!кандидаты.some((о) => о.имя === имя)) {
        const б = (базовыеОчаги || []).find((о) => о?.имя === имя);
        if (б && !отвергнутые.some((х) => х.имя === имя)) отвергнутые.push({ имя, почему: 'в базовом исследовании не найден' });
      }
    }
  }
  // Не больше двух на орган.
  const наОрган = new Map();
  const цели = [];
  let безОргана = 0;
  for (const о of выбранные) {
    const орг = _орган(о.имя);
    if (орг) {
      const было = наОрган.get(орг) || 0;
      if (было >= НА_ОРГАН_МАКС) { отвергнутые.push({ имя: о.имя, почему: `на орган «${орг}» уже ${НА_ОРГАН_МАКС} целевых` }); continue; }
      наОрган.set(орг, было + 1);
    } else безОргана++;
    if (цели.length >= ЦЕЛЕЙ_МАКС) { отвергнутые.push({ имя: о.имя, почему: `целевых уже ${ЦЕЛЕЙ_МАКС} — потолок RECIST` }); continue; }
    цели.push(о.имя);
  }
  if (безОргана) предупреждения.push(`у ${безОргана} очаг(ов) орган из имени не читается («орган-номер») — правило «не больше 2 на орган» для них не проверялось`);
  return { цели, отвергнутые, предупреждения };
}

const _сумма = (очаги, цели) => {
  let с = 0; const нет = [];
  for (const имя of цели) {
    const о = (очаги || []).find((x) => x?.имя === имя);
    if (!о || !Number.isFinite(о.мм)) { нет.push(имя); continue; }
    с += о.мм;
  }
  return { сумма: с, нет };
};

/**
 * Динамика по датам.
 * @param {Array<{дата:string, очаги:Array<{имя,мм,узел?}>, новыеОчаги?:boolean}>} точки
 *        — первая точка считается базовой; даты сортируются строкой (ISO/DICOM).
 * @param {object} [o] — { именаЦелей?: string[] }
 */
export function оценкаRECIST(точки, o = {}) {
  if (!Array.isArray(точки) || точки.length < 1) return { ok: false, причина: 'нет ни одной точки' };
  const ряд = [...точки].sort((а, б) => String(а.дата).localeCompare(String(б.дата)));
  const база = ряд[0];
  const выбор = проверитьЦели(база.очаги, o.именаЦелей || null);
  if (!выбор.цели.length) return { ok: false, причина: 'целевых очагов нет: ' + (выбор.отвергнутые.map((х) => `${х.имя} (${х.почему})`).join('; ') || 'база пуста'), выбор };

  const базоваяСумма = _сумма(база.очаги, выбор.цели).сумма;
  if (!(базоваяСумма > 0)) return { ok: false, причина: 'базовая сумма нулевая — оценивать нечего', выбор };

  const поДатам = [];
  let надир = базоваяСумма;
  for (let i = 0; i < ряд.length; i++) {
    const т = ряд[i];
    const { сумма, нет } = _сумма(т.очаги, выбор.цели);
    let ответ = 'база', почему = 'точка отсчёта';
    if (i > 0) {
      const отБазы = (сумма - базоваяСумма) / базоваяСумма;
      const отНадира = (сумма - надир) / надир;
      const всеНеУзлыНоль = выбор.цели.every((имя) => {
        const о = (т.очаги || []).find((x) => x?.имя === имя);
        const б = (база.очаги || []).find((x) => x?.имя === имя);
        return б?.узел ? true : (о ? о.мм === 0 : false);
      });
      const узлыВНорме = выбор.цели.every((имя) => {
        const б = (база.очаги || []).find((x) => x?.имя === имя);
        if (!б?.узел) return true;
        const о = (т.очаги || []).find((x) => x?.имя === имя);
        return o && о ? о.мм < УЗЕЛ_НОРМА_ММ : false;
      });
      if (т.новыеОчаги) { ответ = 'PD'; почему = 'появились новые очаги'; }
      else if (нет.length) { ответ = 'нет данных'; почему = `не измерены: ${нет.join(', ')}`; }
      else if (всеНеУзлыНоль && узлыВНорме) { ответ = 'CR'; почему = 'все целевые исчезли (узлы < 10 мм короткой оси)'; }
      else if (сумма - надир >= ПД_АБС_ММ && отНадира >= ПД_ДОЛЯ) {
        ответ = 'PD'; почему = `рост от надира ${надир.toFixed(1)} мм на ${(отНадира * 100).toFixed(0)}% и ${(сумма - надир).toFixed(1)} мм`;
      } else if (отБазы <= -ПР_ДОЛЯ) { ответ = 'PR'; почему = `убыль от базы ${(-отБазы * 100).toFixed(0)}%`; }
      else { ответ = 'SD'; почему = 'между порогами PR и PD'; }
    }
    поДатам.push({
      дата: т.дата, сумма,
      отБазыПроц: i ? ((сумма - базоваяСумма) / базоваяСумма) * 100 : 0,
      отНадираПроц: i ? ((сумма - надир) / надир) * 100 : 0,
      надирНаМомент: надир, ответ, почему,
    });
    if (сумма > 0 && сумма < надир) надир = сумма;
    if (сумма === 0) надир = Math.min(надир, ПД_АБС_ММ);   // после CR рост меряется от «почти нуля», не делим на 0
  }
  const последний = поДатам[поДатам.length - 1];
  return { ok: true, цели: выбор.цели, выбор, базоваяСумма, поДатам, итоговыйОтвет: последний.ответ };
}
